import {
  AccordionBody,
  AccordionHeader,
} from 'app/features/questionnaires/edit/TaxQuestionnaire/components/Accordion';
import { IUser } from 'app/services/user';
import DeleteButton from 'Components/Buttons/DeleteButton';
import Hr from 'Components/Hr';
import { ScrollIntoView } from 'Components/ScrollContainer';
import Spinner from 'Components/Spinner';
import UsersList from 'Components/User/UsersList';
import UserAvatarList from 'Components/UserAvatarList';
import UserBox from 'Components/UserBox';
import UserSelectSearch from 'Components/UserSelectSearch';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import Select from 'react-select';

function createUserOptions(users: IUser[]) {
  return users.map((_) => ({
    label: `${_.given_name} ${_.family_name}`,
    value: _.id,
  }));
}

interface UserSelectorProps {
  userSelections: IUser[];
  lockedSelections: IUser[];
  onConfirm?: (all: IUser[], newUsers: IUser[]) => void;
  onChange?: (selectedUsers: IUser[]) => void;
  canDeleteLocked?: boolean;
}
export default function UserSelector({
  userSelections,
  lockedSelections,
  onConfirm,
  onChange,
  canDeleteLocked = true,
}: UserSelectorProps) {
  let recipientIds = lockedSelections.map((r) => r.id);

  const [selected, setSelected] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const [newUsers, setNewUsers] = useState<IUser[]>([]);
  const [locked, setLocked] = useState<IUser[]>(lockedSelections);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [options, setOptions] = useState(
    userSelections.filter((u) => !recipientIds.includes(u.id)),
  );

  useEffect(() => setLocked(lockedSelections), [lockedSelections]);

  const addNewUsers = (option: IUser | null) => {
    if (!option) return;
    let locked = newUsers.concat(
      userSelections.find((u) => u.id === option.id)!,
    );
    setNewUsers(locked);
    setOptions(options.filter((o) => o.id !== option.id));
    setSelected(null);
    if (onChange) onChange([...locked, ...newUsers]);
  };

  const removeLocked = (user: IUser) => {
    let selected = locked.filter((u) => u.id !== user.id);
    setLocked(selected);
    setOptions(options.concat(locked.filter((u) => u.id === user.id)));
    if (onChange) onChange([...locked, ...newUsers]);
  };

  const removeUser = (user: IUser) => {
    let selected = newUsers.filter((u) => u.id !== user.id);
    setNewUsers(selected);
    setOptions(options.concat(userSelections.filter((u) => u.id === user.id)));
    if (onChange) onChange(selected);
  };

  const confirm = () => {
    setConfirmLoading(true);
    if (onConfirm) onConfirm([...locked, ...newUsers], newUsers);
  };

  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [newUsers]);
  return (
    <div className="text-sm md-max:text-xs ">
      <UserSelectSearch skin="name" onSelect={addNewUsers} users={options} />

      <div
        ref={ref}
        className="my-2 h-64 overflow-y-scroll border border-gray-800 rounded-lg"
      >
        <div className="">
          <Accordion allowZeroExpanded>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton className="cursor-pointer px-4 shadow py-2 flex justify-between items-center bg-gray-800 bg-opacity-50">
                  <span className="text-gray-dark font-bold">
                    Users ({locked.length})
                  </span>
                  <UserAvatarList size="sm" users={locked} />
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel className="p-0 bg-gray-800 bg-opacity-30">
                <UsersList
                  users={locked}
                  onDelete={canDeleteLocked ? removeLocked : undefined}
                />
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>
          {newUsers.length > 0 && (
            <div className="text-red-200 font-semibold p-2">New</div>
          )}
          <UsersList users={newUsers} onDelete={removeUser} />
        </div>
      </div>

      {onConfirm && (
        <div className="mt-4 flex justify-end">
          <button
            disabled={confirmLoading}
            className="px-4 py-2 text-sm rounded bg-green text-white disabled:opacity-80 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-green"
            onClick={confirm}
          >
            {confirmLoading ? <Spinner bounce={false} /> : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
}
